import React, { useState } from 'react';
import { login, logout, register, getAuthUser, isAuthenticated } from '../services/api';

// Menu item that shows the current user (with logout) or opens a login modal.
// `onAuthChange` is called after a successful login or logout so the app can
// refresh data; if omitted it falls back to a full page reload.
const Login = ({ onAuthChange }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [identity, setIdentity] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const authed = isAuthenticated();
  const user = getAuthUser();

  const notify = () => {
    if (onAuthChange) onAuthChange();
    else window.location.reload();
  };

  const resetForm = () => {
    setIdentity('');
    setName('');
    setPassword('');
    setConfirm('');
    setError('');
    setInfo('');
  };

  const closeModal = () => {
    setOpen(false);
    setMode('login');
    resetForm();
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (mode === 'register' && password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      if (mode === 'register') {
        await register(identity, password, name);
        setInfo('Account created. An administrator must approve it before you can sign in.');
        setMode('login');
        setName('');
        setPassword('');
        setConfirm('');
      } else {
        await login(identity, password);
        closeModal();
        notify();
      }
    } catch (err) {
      setError(
        mode === 'register'
          ? (err?.message || 'Registration failed.')
          : 'Invalid credentials, account not yet approved, or server unreachable.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    logout();
    notify();
  };

  const linkStyle = {
    textDecoration: 'none',
    color: '#495057',
    fontSize: '1.0rem',
    fontWeight: '500',
    transition: 'color 0.2s ease',
    cursor: 'pointer',
  };

  return (
    <>
      {authed ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: '#495057', fontSize: '1.0rem', fontWeight: '500' }}>
            👤{user || 'User'}
          </span>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleLogout(); }}
            style={{ ...linkStyle, fontSize: '0.8rem', color: '#9ca3af' }}
            onMouseOver={(e) => (e.target.style.color = '#d4af37')}
            onMouseOut={(e) => (e.target.style.color = '#9ca3af')}
            title="Log out"
          >
            (logout)
          </a>
        </span>
      ) : (
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); setOpen(true); }}
          style={linkStyle}
          onMouseOver={(e) => (e.target.style.color = '#d4af37')}
          onMouseOut={(e) => (e.target.style.color = '#495057')}
        >
          👤Login
        </a>
      )}

      {open && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              padding: '24px',
              width: '320px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a' }}>
              {mode === 'register' ? 'Create account' : 'Sign in'}
            </h2>

            <label style={{ fontSize: '0.8rem', color: '#495057' }}>
              {mode === 'register' ? 'Email' : 'Email or username'}
              <input
                type={mode === 'register' ? 'email' : 'text'}
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
                autoFocus
                autoComplete="username"
                style={inputStyle}
              />
            </label>

            {mode === 'register' && (
              <label style={{ fontSize: '0.8rem', color: '#495057' }}>
                Name
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  style={inputStyle}
                />
              </label>
            )}

            <label style={{ fontSize: '0.8rem', color: '#495057' }}>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                style={inputStyle}
              />
            </label>

            {mode === 'register' && (
              <label style={{ fontSize: '0.8rem', color: '#495057' }}>
                Confirm password
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  style={inputStyle}
                />
              </label>
            )}

            {error && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem' }}>{error}</div>
            )}
            {info && (
              <div style={{ color: '#16a34a', fontSize: '0.8rem' }}>{info}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={closeModal}
                style={{ ...buttonStyle, backgroundColor: '#f3f4f6', color: '#495057' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !identity || !password || (mode === 'register' && (!confirm || !name))}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#d4af37',
                  color: '#1a1a1a',
                  opacity: busy || !identity || !password || (mode === 'register' && (!confirm || !name)) ? 0.6 : 1,
                }}
              >
                {busy
                  ? (mode === 'register' ? 'Creating…' : 'Signing in…')
                  : (mode === 'register' ? 'Create account' : 'Sign in')}
              </button>
            </div>

            <div style={{ fontSize: '0.78rem', color: '#9ca3af', textAlign: 'center' }}>
              {mode === 'register' ? (
                <>
                  Already have an account?{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); switchMode('login'); }} style={{ color: '#d4af37' }}>
                    Sign in
                  </a>
                </>
              ) : (
                <>
                  No account?{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); switchMode('register'); }} style={{ color: '#d4af37' }}>
                    Register
                  </a>
                </>
              )}
            </div>
          </form>
        </div>
      )}
    </>
  );
};

const inputStyle = {
  width: '100%',
  marginTop: '4px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};

const buttonStyle = {
  padding: '8px 14px',
  borderRadius: '6px',
  border: 'none',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export default Login;
